import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { BillOut } from "../../../../../api/bills";
import { getUsers, type UserListItemOut } from "../../../../../api/auth";
import { useAppSelector } from "../../../../../store/hooks";
import {
  billShownForWaiter,
  followedWaiterOf,
  orderWaiterOptions,
  waiterIdsWithBills,
} from "./orderingFloorData";

const NO_USERS: UserListItemOut[] = [];

export interface SchemaWaiterOption {
  waiterId: number;
  name: string | null;
}

/** Полето „Сервитьор" — опциите, показаният и изборът. */
export interface SchemaWaiterChoice {
  options: SchemaWaiterOption[];
  selectedWaiterId: number;
  pick: (waiterId: number) => void;
  /** Изборът на друг сервитьор маха избраната маса и сметка (лична номерация —
   *  чуждите сметки изчезват от залата); при обща — изборът остава. */
  clearsSelection: boolean;
  /** Отваряне на падащото меню — пресни сметки (кой още има маси). */
  refresh: () => void;
}

/**
 * Полето „Сервитьор" в режим „Схема" (горе вдясно): само с право
 * `view_all_users_bills` и поне още един сервитьор със сметка в списъка
 * (отворена или нефискализирана — каквото списъкът показва). По подразбиране —
 * влезлият. Кой сервитьор е „на фокус" (`focusWaiterId`) зависи от
 * номерацията на масите (Конфигурация → Маркиране):
 *
 * - ОБЩА номерация — залата и лентите показват ВСИЧКИ сметки (като списъка):
 *   чуждите са бледи, но се избират и в тях се работи както в своите. Полето
 *   сменя само кой е на фокус; не следва избраната сметка и не маха избора.
 * - ЛИЧНА номерация — двама може да имат маса „1": залата и лентите показват
 *   САМО сметките на показания сервитьор (+ ничиите от „Настани"); чуждите се
 *   виждат само като се избере сервитьорът им. Избрана отвън чужда сметка
 *   (поръчка, глас, „Настани", „Смяна на сервитьор") показва своя сервитьор;
 *   влизане в режима с избрана чужда сметка я маха.
 *
 * Изчезне ли избраният от опциите — обратно на влезлия. Само в паметта.
 * Без полето — същите масиви, фокусът е влезлият.
 */
export const useSchemaWaiterFilter = ({
  enabled,
  tableBills,
  sortedBills,
  selectedBill,
  sharedNumbering,
  isTablesView,
  onClearSelection,
}: {
  enabled: boolean;
  /** На екрана с масите (не в менюто) — само там влизането маха чужда сметка. */
  isTablesView: boolean;
  tableBills: BillOut[];
  sortedBills: BillOut[];
  selectedBill: BillOut | null;
  /** Обща номерация на масите (настройка на ресторанта). */
  sharedNumbering: boolean;
  /** Маха избраната сметка (при влизане в режима с чужда). */
  onClearSelection: () => void;
}) => {
  const queryClient = useQueryClient();
  const currentUserId = useAppSelector((state) => state.auth.userId);
  const selectedBillId = useAppSelector((state) => state.bills.selectedBillId);
  const ownLabel = useAppSelector((state) => state.auth.names || state.auth.username);
  const seesAllBills = useAppSelector((state) =>
    state.auth.permissions.some((permission) => permission.name === "view_all_users_bills"),
  );
  const usable = enabled && seesAllBills && currentUserId !== null;

  const [picked, setPicked] = useState<{ userId: number; waiterId: number } | null>(null);
  const pickedWaiterId = picked && picked.userId === currentUserId ? picked.waiterId : null;

  const optionIds = useMemo(
    () => (usable && currentUserId !== null ? waiterIdsWithBills(sortedBills, currentUserId, selectedBill) : []),
    [usable, sortedBills, currentUserId, selectedBill],
  );
  const filterOn = optionIds.length >= 2;

  // Сметката, избрана в момента на влизане в режима (от списъка или останала
  // от преди) — изчислено още в рендера (без ефекти и рефове), за да не се
  // следва нито за миг. Само при лична номерация и на екрана с масите (не в
  // менюто — там се поръчва по нея).
  const [entry, setEntry] = useState<{ enabled: boolean; billId: number | null }>({
    enabled: false,
    billId: null,
  });
  let entryBillId = entry.billId;
  if (entry.enabled !== enabled) {
    entryBillId = enabled && isTablesView && !sharedNumbering ? selectedBillId : null;
    setEntry({ enabled, billId: entryBillId });
  }
  const followedOfSelected = followedWaiterOf(selectedBill);
  // Чужда сметка, избрана преди влизането — маха се, не се следва.
  const clearsEntryBill =
    filterOn &&
    !sharedNumbering &&
    selectedBill !== null &&
    selectedBill.id === entryBillId &&
    followedOfSelected !== null &&
    followedOfSelected !== currentUserId;

  // Следване на избраната сметка — само при лична номерация (там чуждата
  // сметка иначе не е в залата).
  const followedWaiterId = filterOn && !sharedNumbering && !clearsEntryBill ? followedOfSelected : null;
  const effectiveWaiterId =
    followedWaiterId ??
    (pickedWaiterId !== null && optionIds.includes(pickedWaiterId) ? pickedWaiterId : null) ??
    currentUserId;

  // Влизане с избрана чужда сметка → изборът се маха (масата ѝ — също, в
  // `useSchemaSelection`: тя не следва непоказана сметка).
  useEffect(() => {
    if (clearsEntryBill) onClearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearsEntryBill]);
  // Сметката от влизането вече не е избрана — правилото приключва.
  useEffect(() => {
    if (entry.billId !== null && selectedBillId !== entry.billId) {
      setEntry((previous) => ({ ...previous, billId: null }));
    }
  }, [entry.billId, selectedBillId]);

  // Полето следва избраната отвън сметка — запомня се, за да остане след нея.
  useEffect(() => {
    if (followedWaiterId === null || currentUserId === null) return;
    setPicked((previous) =>
      previous && previous.userId === currentUserId && previous.waiterId === followedWaiterId
        ? previous
        : { userId: currentUserId, waiterId: followedWaiterId },
    );
  }, [followedWaiterId, currentUserId]);

  // Избраният вече няма сметки — обратно на влезлия.
  useEffect(() => {
    if (pickedWaiterId !== null && !optionIds.includes(pickedWaiterId)) setPicked(null);
  }, [pickedWaiterId, optionIds]);

  // Имената — само когато полето се показва (`GET /user/users` е в одита);
  // кешът е общ с `useUsers()`.
  const { data: users = NO_USERS } = useQuery({
    queryKey: ["users", "all", false, "none"],
    queryFn: () => getUsers(),
    enabled: filterOn,
    staleTime: 5 * 60_000,
  });
  const waiterNameOf = useCallback(
    (waiterId: number | null) => {
      if (waiterId === null) return null;
      const user = users.find((candidate) => candidate.id === waiterId);
      if (user) return user.names || user.username;
      return waiterId === currentUserId ? ownLabel : null;
    },
    [users, currentUserId, ownLabel],
  );

  // Лична номерация с полето — само сметките на показания (+ ничиите).
  const filtersBills = filterOn && !sharedNumbering && effectiveWaiterId !== null;
  const shownSortedBills = useMemo(
    () =>
      filtersBills && effectiveWaiterId !== null
        ? sortedBills.filter((bill) => billShownForWaiter(bill, effectiveWaiterId))
        : sortedBills,
    [filtersBills, effectiveWaiterId, sortedBills],
  );
  const shownTableBills = useMemo(
    () =>
      filtersBills && effectiveWaiterId !== null
        ? tableBills.filter((bill) => billShownForWaiter(bill, effectiveWaiterId))
        : tableBills,
    [filtersBills, effectiveWaiterId, tableBills],
  );

  const choice: SchemaWaiterChoice | null =
    filterOn && effectiveWaiterId !== null && currentUserId !== null
      ? {
          options: orderWaiterOptions(
            optionIds.map((waiterId) => ({ waiterId, name: waiterNameOf(waiterId) })),
            currentUserId,
          ),
          selectedWaiterId: effectiveWaiterId,
          pick: (waiterId) => setPicked({ userId: currentUserId, waiterId }),
          clearsSelection: !sharedNumbering,
          refresh: () => void queryClient.invalidateQueries({ queryKey: ["bills"] }),
        }
      : null;

  return {
    sortedBills: shownSortedBills,
    tableBills: shownTableBills,
    /** Кой е на фокус: показаният в полето, иначе влезлият. */
    focusWaiterId: effectiveWaiterId,
    choice,
  };
};
